import React, { useState, useEffect, useRef } from 'react';

function DeliveryDetails({ delivery, setDelivery, isLoaded }) {
    const [city, setCity] = useState('');
    const [streetAddress, setStreetAddress] = useState('');
    const [notes, setNotes] = useState('');

    const cityInputRef = useRef(null);
    const streetInputRef = useRef(null);

    const cityAutocompleteRef = useRef(null);
    const streetAutocompleteRef = useRef(null);
    const cityBoundsRef = useRef(null);

    // 🔁 Sync from parent
    useEffect(() => {
        if (delivery) {
            setCity(delivery.city || '');
            setStreetAddress(delivery.street || '');
            setNotes(delivery.notes || '');
        } else {
            setCity('');
            setStreetAddress('');
            setNotes('');
        }
    }, [delivery]);

    // 🌍 City Autocomplete
    useEffect(() => {
        if (!isLoaded || !window.google?.maps?.places) return;

        if (!cityAutocompleteRef.current && cityInputRef.current) {
            cityAutocompleteRef.current =
                new window.google.maps.places.Autocomplete(
                    cityInputRef.current,
                    {
                        types: ['(cities)'],
                        componentRestrictions: { country: 'KE' },
                    }
                );

            cityAutocompleteRef.current.addListener('place_changed', () => {
                const place = cityAutocompleteRef.current.getPlace();
                const cityName = place?.name || cityInputRef.current.value;

                setCity(cityName);
                setDelivery(prev => ({ ...prev, city: cityName }));

                // ✅ Save city bounds for street biasing
                if (place?.geometry?.viewport) {
                    cityBoundsRef.current = place.geometry.viewport;
                }
            });
        }

        return () => {
            if (cityAutocompleteRef.current) {
                window.google.maps.event.clearInstanceListeners(
                    cityAutocompleteRef.current
                );
            }
        };
    }, [isLoaded, setDelivery]);

    useEffect(() => {
        if (
            !isLoaded ||
            !window.google?.maps?.places ||
            !city.trim() ||
            !streetInputRef.current
        )
            return;

        // Reset on city change
        if (streetAutocompleteRef.current) {
            window.google.maps.event.clearInstanceListeners(
                streetAutocompleteRef.current
            );
            streetAutocompleteRef.current = null;
        }

        streetAutocompleteRef.current =
            new window.google.maps.places.Autocomplete(
                streetInputRef.current,
                {
                    componentRestrictions: { country: 'KE' },
                    fields: ['formatted_address', 'geometry', 'name'],
                }
            );

        // Bias results to selected city
        if (cityBoundsRef.current) {
            streetAutocompleteRef.current.setBounds(cityBoundsRef.current);
            streetAutocompleteRef.current.setOptions({ strictBounds: false });
        }

        streetAutocompleteRef.current.addListener('place_changed', () => {
            const place = streetAutocompleteRef.current.getPlace();

            // Use best available label
            const address =
                place.name  ||
                place.formatted_address ||
                streetInputRef.current.value;

            if (place?.geometry?.location) {
                const lat = parseFloat(place.geometry.location.lat().toFixed(8))
                const lng = parseFloat(place.geometry.location.lng().toFixed(8))

                setStreetAddress(address);
                setDelivery(prev => ({
                    ...prev,
                    street: address,
                    latitude: lat,
                    longitude: lng,
                }));
            } else {
                // Manual fallback
                setStreetAddress(address);
                setDelivery(prev => ({
                    ...prev,
                    street: address,
                    latitude: null,
                    longitude: null,
                }));
            }
        });

        return () => {
            if (streetAutocompleteRef.current) {
                window.google.maps.event.clearInstanceListeners(
                    streetAutocompleteRef.current
                );
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, city, setDelivery]);

    const handleBlur = (field, value) => {
        setDelivery(prev => ({ ...prev, [field]: value.trim() }));
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row gap-5 mb-3">
                {/* City */}
                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">City/Town: *</span>
                    </label>
                    {isLoaded ? (
                        <input
                            ref={cityInputRef}
                            value={city}
                            onChange={e => setCity(e.target.value)}
                            onBlur={() => handleBlur('city', city)}
                            type="text"
                            placeholder="e.g. Nairobi, Mombasa"
                            className="input input-bordered w-full"
                            required
                        />
                    ) : (
                        <div className="input input-bordered w-full bg-gray-200 text-gray-500">
                            Loading...
                        </div>
                    )}
                </div>

                {/* Street Address */}
                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">Street Address: *</span>
                    </label>
                    <input
                        ref={streetInputRef}
                        value={streetAddress}
                        onChange={e => setStreetAddress(e.target.value)}
                        onBlur={() => handleBlur('street', streetAddress)}
                        type="text"
                        placeholder="House number, estate, building"
                        className="input input-bordered w-full"
                        required
                    />
                </div>
            </div>

            {/* Notes */}
            <div className="form-control mb-3">
                <label className="label">
                    <span className="label-text">
                        Order Notes (Optional)
                    </span>
                </label>
                <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    onBlur={() => handleBlur('notes', notes)}
                    placeholder="e.g. Near Shell petrol station, gate code 1234"
                    className="input input-bordered w-full h-20 resize-none"
                />
            </div>
        </div>
    );
}

export default DeliveryDetails;
